import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";
import type {
  CashierPaymentMethod,
  CashierPaymentMethodGroup,
  SavedPaymentAccountIdAndFields,
} from "@/api/cashier";
import { ApplePayButton } from "@/components/apple-pay-button/ApplePayButton";
import { DepositBankNotice } from "@/components/deposit-bank-notice/DepositBankNotice";
import { ErrorMessage } from "@/components/ErrorMessage";
import { EnterAmountSection } from "@/components/enter-amount-section/EnterAmountSection";
import { GridPageLayout } from "@/components/general/grid-page-layout/GridPageLayout";
import { InfoBox } from "@/components/general/info-box/InfoBox";
import { MobileBottomSheet } from "@/components/general/mobile-bottom-sheet/MobileBottomSheet";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { ScrollToTopButton } from "@/components/general/scroll-to-top-button/ScrollToTopButton";
import {
  Section,
  SectionActions,
  SectionHeader,
  SectionTitle,
} from "@/components/general/section";
import { GooglePayButton } from "@/components/google-pay-button/GooglePayButton";
import { MethodList } from "@/components/method-list/MethodList";
import { InstructionsModal } from "@/components/modals/instructions-modal/InstructionsModal";
import { PayPalButton } from "@/components/paypal-button/PayPalButton";
import { useAccount } from "@/hooks/deposit/useAccount";
import {
  selectAmountFormData,
  useAmountForm,
} from "@/hooks/deposit/useAmountForm";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useCurrencyConverter } from "@/hooks/deposit/useCurrencyConverter";
import { useDynamicForm } from "@/hooks/deposit/useDynamicForm";
import { useInitPaymentMethods } from "@/hooks/deposit/useInitPaymentMethods";
import { useMethod } from "@/hooks/deposit/useMethod";
import { usePspFlowHandler } from "@/hooks/deposit/usePspFlowHandler";
import { useDelayedUnmount } from "@/hooks/useDelayedUnmount";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useIsScrolledPastTarget } from "@/hooks/useIsScrolledPastTarget";
import { useLogger } from "@/hooks/useLogger";
import { useUserData } from "@/hooks/useUserData";
import { DEPOSIT_INSTRUCTIONS } from "@/mock/Instructions";
import { useCreditCardPayment } from "@/pages/deposit/choose-method/useCreditCardPayment";
import { DepositNotAvailable } from "@/pages/deposit/DepositNotAvailable";
import {
  FunnelFlow,
  FunnelStep,
  trackFunnelStep,
} from "@/observability/funnel";
import { useAppStore } from "@/redux/hooks";
import { ERoutes } from "@/utils/enums";
import {
  getPaymentMethodFormFields,
  isApplePay,
  isBankTransfer,
  isCardForm,
  isGooglePay,
  isPayPal,
} from "@/utils/helpers";
import { isExchangeRateTechnicalIssue } from "@/utils/paymentMethodStatus";
import styles from "./ChooseMethod.module.css";
import { useApmPayment } from "./useApmPayment";

/**
 * Translation key declarations for i18next-parser extraction.
 * This key can be surfaced dynamically via API error handling for saved payment
 * accounts, so we declare it statically here for the parser to detect it.
 * This code never executes but is required for the build process.
 */
// t("cashier.saved_payment_account.new_spa_matched_spa_with_deletion_request");
// t("cashier.saved_payment_account.new_spa_matched_spa_with_invalid_status");

export const ChooseMethod = () => {
  const { t, i18n } = useTranslation();
  const logger = useLogger();
  const isMobile = useIsMobile();
  const methodSectionRef = useRef<HTMLElement>(null);
  const {
    data: paymentMethods,
    isLoading: paymentMethodsLoading,
    error,
    refetch,
  } = useInitPaymentMethods();
  const isScrolledPastTarget = useIsScrolledPastTarget(methodSectionRef);
  const shouldShowBottomSheet = isMobile && !isScrolledPastTarget;
  const { isMounted: isBottomSheetMounted, isLeaving: isBottomSheetLeaving } =
    useDelayedUnmount(shouldShowBottomSheet);

  const navigate = useNavigate();
  const { data: userData } = useUserData();
  const { selectedAccount } = useAccount();
  const {
    setSelectedPaymentMethod,
    setSelectedPaymentMethodGroup,
    setSelectedSavedPaymentAccount,
    selectedPaymentMethod,
    selectedSavedPaymentAccount,
  } = useMethod();
  const { handlePaymentMethodSetWithConversion } = useCurrencyConverter();
  const store = useAppStore();
  const {
    updateAmountFormData,
    amountFormIsValid,
    getAmountFormIsValid,
    amountFormData,
  } = useAmountForm();
  const { getFormData, getIsValid } = useDynamicForm();
  const [conversionError, setConversionError] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount
  useEffect(() => {
    trackFunnelStep(FunnelFlow.Deposit, FunnelStep.Intent);
  }, []);
  const [isAddingNewAccount, setIsAddingNewAccount] = useState(false);
  const paymentMethodId = selectedPaymentMethod?.id;

  const paymentMethodFormData = paymentMethodId
    ? getFormData(paymentMethodId)
    : undefined;

  const paymentMethodFormValid = paymentMethodId
    ? getIsValid(paymentMethodId)
    : undefined;

  const paymentMethodFormFields = paymentMethodId
    ? getPaymentMethodFormFields(selectedPaymentMethod, userData.license_id)
    : undefined;
  const methodHasFormFields = (paymentMethodFormFields?.length ?? 0) > 0;
  const currentPaymentMethodFormData = methodHasFormFields
    ? paymentMethodFormData
    : undefined;

  const hasSavedAccounts = Boolean(
    selectedPaymentMethod?.saved_payment_accounts?.length
  );

  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const [pendingMobileSheetOpen, setPendingMobileSheetOpen] = useState(false);
  const bottomSheetAmountSectionRef = useRef<HTMLDivElement>(null);

  const paymentMethodInputReady = hasSavedAccounts
    ? Boolean(
        selectedSavedPaymentAccount ||
          (isAddingNewAccount &&
            (methodHasFormFields
              ? Boolean(currentPaymentMethodFormData) &&
                paymentMethodFormValid === true
              : true))
      )
    : Boolean(
        selectedPaymentMethod &&
          (selectedSavedPaymentAccount ||
            !methodHasFormFields ||
            (isMobile
              ? Boolean(currentPaymentMethodFormData) &&
                paymentMethodFormValid === true
              : paymentMethodFormValid))
      );

  const { selectedProcessingCurrency, currencies } = useCurrencies();

  const {
    handleApplePayClick,
    handleGooglePayClick,
    handleApmDeferredNextClick,
    handlePayPalSpaPayment,
    processPayPalPayment,
    apmDeferredReady,
    apmDeferredSessionToken,
    apmDeferredSessionEnv,
    handleApmPaymentError,
    apmPaymentError,
    isPending: isApmPaymentPending,
  } = useApmPayment({
    onPaymentSuccess: (transactionId) =>
      navigate(`../${ERoutes.Completion}/${transactionId}`),
    // The backend persisted the transaction as failed — the completion page
    // renders the unsuccessful thank-you state from the transaction status.
    onPaymentFailure: (transactionId) =>
      navigate(`../${ERoutes.Completion}/${transactionId}`),
  });

  const {
    creditCardPayment,
    creditCardFormValid,
    isPending: processingCardPayment,
    error: creditCardPaymentError,
    flowState: creditCardPaymentFlow,
    resetPaymentFlow: resetCreditCardPaymentFlow,
    transactionId,
    shouldContinueToTYP,
    isRestrictedBinCode,
  } = useCreditCardPayment();

  const {
    handleFlow,
    error: pspFlowError,
    resetError: resetPspFlowError,
    isPending: processingPspFlow,
    importantNotesModal,
  } = usePspFlowHandler();

  const handlePaymentMethodChange = useCallback(
    async (
      paymentMethod: CashierPaymentMethod,
      paymentMethodGroup?: CashierPaymentMethodGroup,
      savedPaymentAccount?: SavedPaymentAccountIdAndFields,
      isUserInitiated = true
    ) => {
      setSelectedPaymentMethod(paymentMethod);
      setSelectedPaymentMethodGroup(paymentMethodGroup);
      setSelectedSavedPaymentAccount(savedPaymentAccount);

      if (!selectedAccount) {
        return;
      }

      if (!currencies) {
        return;
      }

      const currencyCodes =
        paymentMethod.processing_currencies ??
        Object.keys(paymentMethod.settings ?? {});
      const availableCurrencies = currencyCodes
        .map((currencyCode) => currencies[currencyCode])
        .filter(Boolean);

      // Prefer trading account currency if supported by payment method,
      // otherwise use first supported currency, fallback to trading currency
      const initialCurrency =
        availableCurrencies.find(
          (curr) => curr.code === selectedAccount.currency.code
        ) ??
        availableCurrencies[0] ??
        selectedAccount.currency;

      updateAmountFormData({
        selectedProcessingCurrency: initialCurrency,
      });

      const latestTradingValue =
        selectAmountFormData(store.getState()).tradingValue ?? 0;

      const { isSuccess, isCancelled = false } =
        await handlePaymentMethodSetWithConversion({
          processingCurrency: initialCurrency,
          paymentMethod: { method: paymentMethod, group: paymentMethodGroup },
          selectedAccount,
          selectedPaymentMethod: paymentMethod,
          amount: latestTradingValue,
          // isReverse: true,
        });

      // A superseded (cancelled) request isn't a real failure - e.g. the user
      // switched payment methods again before this one resolved. Only a
      // genuine failure of the latest request should surface the banner.
      if (!isCancelled) {
        setConversionError(!isSuccess);
      }

      if (creditCardPaymentFlow === "error") {
        resetCreditCardPaymentFlow();
      }
      resetPspFlowError();

      if (isMobile && selectedAccount && isUserInitiated) {
        setPendingMobileSheetOpen(true);
      }
    },
    [
      creditCardPaymentFlow,
      resetCreditCardPaymentFlow,
      resetPspFlowError,
      handlePaymentMethodSetWithConversion,
      selectedAccount,
      updateAmountFormData,
      setSelectedPaymentMethod,
      setSelectedPaymentMethodGroup,
      setSelectedSavedPaymentAccount,
      currencies,
      store,
    ]
  );

  useEffect(() => {
    if (
      currencies &&
      selectedPaymentMethod &&
      selectedAccount &&
      !selectedProcessingCurrency
    ) {
      handlePaymentMethodChange(
        selectedPaymentMethod,
        undefined,
        selectedSavedPaymentAccount,
        false
      );
    }
  }, [
    currencies,
    selectedPaymentMethod,
    selectedAccount,
    selectedProcessingCurrency,
    selectedSavedPaymentAccount,
    handlePaymentMethodChange,
  ]);

  const submit = useCallback(() => {
    if (!selectedPaymentMethod || !getAmountFormIsValid()) return;

    trackFunnelStep(FunnelFlow.Deposit, FunnelStep.ExecuteClicked);

    const isCard = isCardForm(selectedPaymentMethod);
    const isGooglePayMethod = isGooglePay(selectedPaymentMethod);
    const isApplePayMethod = isApplePay(selectedPaymentMethod);
    const isPayPalMethod = isPayPal(selectedPaymentMethod);
    const hasSavedAccount = Boolean(selectedSavedPaymentAccount);

    if (
      !isCard &&
      !isGooglePayMethod &&
      !isApplePayMethod &&
      !paymentMethodInputReady
    )
      return;

    if (isCard) {
      if (creditCardPaymentFlow === "error") {
        resetCreditCardPaymentFlow();
      }
      creditCardPayment();
      return;
    }

    if (isGooglePayMethod) return; // Google Pay button handles flow
    if (isPayPalMethod) {
      if (hasSavedAccount) {
        handlePayPalSpaPayment();
      } else {
        handleApmDeferredNextClick();
      }
      return;
    }
    if (isApplePayMethod) {
      handleApmDeferredNextClick();
      return;
    }

    handleFlow(currentPaymentMethodFormData);
  }, [
    selectedPaymentMethod,
    getAmountFormIsValid,
    selectedSavedPaymentAccount,
    paymentMethodInputReady,
    creditCardPaymentFlow,
    creditCardPayment,
    handleFlow,
    handleApmDeferredNextClick,
    handlePayPalSpaPayment,
    currentPaymentMethodFormData,
    resetCreditCardPaymentFlow,
  ]);

  // navigate to TYP when CC deposit is successful
  useEffect(() => {
    if (shouldContinueToTYP) {
      navigate(`../${ERoutes.Completion}/${transactionId}`);
    }
  }, [shouldContinueToTYP, transactionId, navigate]);

  const isCardFlow = isCardForm(selectedPaymentMethod);
  const isGooglePayFlow = isGooglePay(selectedPaymentMethod);
  const isApplePayFlow = isApplePay(selectedPaymentMethod);
  const isPayPalFlow = isPayPal(selectedPaymentMethod);
  const isSubmitting =
    processingCardPayment || processingPspFlow || isApmPaymentPending;
  const paymentMethodInputsReady = isCardFlow
    ? Boolean(creditCardFormValid || selectedSavedPaymentAccount)
    : isGooglePayFlow || isApplePayFlow
      ? true
      : paymentMethodInputReady;

  useEffect(() => {
    if (pendingMobileSheetOpen && paymentMethodInputsReady) {
      setIsSectionOpen(true);
      setPendingMobileSheetOpen(false);
    }
  }, [pendingMobileSheetOpen, paymentMethodInputsReady]);

  useEffect(() => {
    if (!isBottomSheetMounted || !isSectionOpen) return;
    bottomSheetAmountSectionRef.current?.querySelector("input")?.focus();
  }, [isBottomSheetMounted, isSectionOpen]);

  const pulsateBottomSheet = useMemo(
    () => isMobile && pendingMobileSheetOpen && !paymentMethodInputsReady,
    [isMobile, pendingMobileSheetOpen, paymentMethodInputsReady]
  );

  const isDisabled =
    isSubmitting ||
    !amountFormIsValid ||
    (isCardFlow && !(creditCardFormValid || selectedSavedPaymentAccount)) ||
    (!isCardFlow &&
      !isGooglePayFlow &&
      !isApplePayFlow &&
      !isPayPalFlow &&
      !paymentMethodInputReady);

  const defaultInstructionIndex = useMemo(() => {
    const foundIdx = DEPOSIT_INSTRUCTIONS.findIndex(
      // TODO: find out should we should use headerTitle or ariaControls here. Same for the display_name or any other property
      (item) => item.headerTitle === selectedPaymentMethod?.display_name
    );
    if (foundIdx === -1) return 0;
    return foundIdx;
  }, [selectedPaymentMethod]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        submit();
      }
    };

    window.addEventListener("keydown", handleKey);

    return () => window.removeEventListener("keydown", handleKey);
  }, [submit]);

  // TODO: quick fix for demo, get selected account from url and move the error screen check before this condition
  if (!selectedAccount) {
    return <Navigate to="/deposit/accounts" replace />;
  }

  if (
    error ||
    (!paymentMethodsLoading &&
      paymentMethods &&
      !paymentMethods?.arrangement?.length)
  ) {
    logger.error("Deposit not available, unable to fetch payment methods.", {
      error,
      paymentMethods,
    });
    return <DepositNotAvailable />;
  }

  // Show the "currency exchange unavailable" banner when either the live
  // /cashier/v1/exchange-rate conversion failed, or the backend marked any
  // payment method offline because it couldn't fetch a rate while computing
  // limits/fees (disabled_reason: technical_issue).
  const exchangeRateUnavailable =
    conversionError ||
    (paymentMethods?.arrangement ?? []).some((method) =>
      "payment_methods" in method
        ? method.payment_methods.some(isExchangeRateTechnicalIssue)
        : isExchangeRateTechnicalIssue(method)
    );

  const amountSectionContent = (
    <>
      <SectionHeader>
        <SectionTitle>{t("deposit.methods.enter_amount.title")}</SectionTitle>
      </SectionHeader>
      <EnterAmountSection
        isLoading={paymentMethodsLoading}
        disabled={isSubmitting}
      />
      {/* HERE add the deposit bank notice */}
      {isBankTransfer(selectedPaymentMethod) && (
        <DepositBankNotice
          fee={0}
          currency={selectedAccount.currency}
          className={styles.bankNotice}
        >
          {t("deposit.bank_transfer_indicative_disclaimer")}
        </DepositBankNotice>
      )}
      <NavButtons
        nextButton={
          isGooglePayFlow && selectedProcessingCurrency ? (
            <GooglePayButton
              onDeferredClick={handleGooglePayClick}
              amount={String(amountFormData.processingValue ?? "")}
              currencyCode={selectedProcessingCurrency.code}
              onError={handleApmPaymentError}
              disabled={!amountFormIsValid || isApmPaymentPending}
            />
          ) : isApplePayFlow &&
            apmDeferredReady &&
            selectedProcessingCurrency ? (
            <ApplePayButton
              onApplePayClick={handleApplePayClick}
              onError={handleApmPaymentError}
              disabled={isApmPaymentPending}
            />
          ) : isPayPalFlow &&
            apmDeferredReady &&
            apmDeferredSessionToken &&
            selectedProcessingCurrency ? (
            <PayPalButton
              clientToken={apmDeferredSessionToken}
              env={apmDeferredSessionEnv}
              amount={String(amountFormData.processingValue ?? "")}
              currencyCode={selectedProcessingCurrency.code}
              onSuccess={processPayPalPayment}
              onError={handleApmPaymentError}
              disabled={!amountFormIsValid || isApmPaymentPending}
            />
          ) : undefined
        }
        nextLabel={t("global.nav_buttons.next")}
        nextAction={submit}
        backAction={() => navigate(`../${ERoutes.Accounts}`)}
        nextDisabled={isDisabled}
        backDisabled={isSubmitting}
        nextIsLoading={isSubmitting}
      />
      {creditCardPaymentError && (
        <ErrorMessage
          message={
            isRestrictedBinCode
              ? t("deposit.restricted_deposit_method")
              : i18n.exists(creditCardPaymentError)
                ? t(creditCardPaymentError)
                : t("deposit.transaction_cannot_be_started")
          }
        />
      )}
      <ErrorMessage message={apmPaymentError} />
      {pspFlowError && (
        <ErrorMessage
          message={
            i18n.exists(pspFlowError)
              ? t(pspFlowError)
              : t("deposit.transaction_cannot_be_started")
          }
        />
      )}
      {conversionError && (
        <ErrorMessage message={t("deposit.methods.currency_exchange_error")} />
      )}
    </>
  );

  return (
    <GridPageLayout
      banner={
        exchangeRateUnavailable && (
          <InfoBox
            variant="warning"
            data-testid="deposit-exchange-rate-banner"
            title={t("global.exchange_rate_unavailable.title")}
            description={t("global.exchange_rate_unavailable.description")}
          />
        )
      }
    >
      <Section ref={methodSectionRef}>
        <SectionHeader>
          <SectionTitle>{t("deposit.methods.select_method")}</SectionTitle>
          <SectionActions>
            <InstructionsModal.Button
              data-testid="InstructionsModal"
              asAccordions
              // TODO: replace the static instructions with the ones that are available from the API, if needed
              instructions={DEPOSIT_INSTRUCTIONS}
              defaultInstructionIndex={defaultInstructionIndex}
              title={t("deposit.methods.instructions")}
              fixedHeight={"536px"}
            >
              {t("deposit.methods.instructions")}
            </InstructionsModal.Button>
          </SectionActions>
        </SectionHeader>
        <MethodList
          isLoading={paymentMethodsLoading}
          disabled={isSubmitting}
          paymentMethods={paymentMethods?.arrangement}
          lastUsedPaymentMethod={paymentMethods?.last_used_payment_method}
          tradingCurrency={selectedAccount.currency}
          licenceId={userData.license_id}
          selectedPaymentMethodChange={handlePaymentMethodChange}
          onPaymentMethodsUpdated={() => refetch()}
          onAddingNewAccountChange={setIsAddingNewAccount}
        />
      </Section>
      {isBottomSheetMounted ? (
        <div
          className={clsx(
            styles.bottomSheetGroup,
            styles.bottomSheetGroupEnter,
            isBottomSheetLeaving && styles.bottomSheetGroupLeaving
          )}
        >
          <MobileBottomSheet
            isDisabled={isSubmitting}
            canOpen={paymentMethodInputsReady}
            isOpen={isSectionOpen}
            onOpenChange={setIsSectionOpen}
            pulsating={pulsateBottomSheet}
          >
            <div
              ref={bottomSheetAmountSectionRef}
              className={styles.bottomSheetAmountSection}
            >
              {amountSectionContent}
            </div>
          </MobileBottomSheet>
        </div>
      ) : (
        <Section
          className={clsx(styles.stickySection, styles.stickySectionEnter)}
        >
          {amountSectionContent}
        </Section>
      )}
      {!isSectionOpen && isMobile && (
        <ScrollToTopButton targetRef={methodSectionRef} />
      )}
      {importantNotesModal}
    </GridPageLayout>
  );
};
